import React, { useState } from 'react';
import { enqueueSnackbar } from 'src/reducers/notify';
import massActionApi from 'src/utils/api/massActionApi';
import apiClient from 'src/utils/apiClient';
import massActionUtils from './utils';
import contentEditorUtils from 'src/components/common/contentEditor/utils/contentEditorUtils';
import VerificationCodeForm from 'src/components/common/VerificationCodeForm';

import paramSelectors from 'src/selectors/parameters';
import utils from 'src/utils/utils';

import { useLoader } from 'src/reducers/loader';
import ActionType from 'src/enums/actionType';
import { useDispatch, useSelector } from 'react-redux';
import { useAlert, useDidMount } from 'src/hooks';
import { onSaveCommercialRelaunchAction } from './commercialMassActionEditorActions';
import i18next from 'i18next';
import useApiCaller from 'src/hooks/useApiCaller';
import { useMassActionState } from 'src/components/massAction/MassActionStateContext';

const getUngroupedSelectedColumns = selectedResultfields =>
  Object.values(selectedResultfields).reduce(
    (ungrouped, current) => [...ungrouped, ...current.map(column => column.value)],
    []
  );

const getContactIdResultfield = availableResultfields =>
  availableResultfields.contact.filter(({ value }) => value === 'field0')[0];

const getContentFields = ({
  selectedResultfields,
  defaultContentFields,
  forbiddenContentFields,
}) => {
  const selectedFields = Object.values(selectedResultfields).reduce(
    (acc, currentFieldGroup) => ({
      ...acc,
      ...currentFieldGroup.reduce(
        (acc2, currentField) => ({ ...acc2, [currentField.value]: currentField.type }),
        {}
      ),
    }),
    {}
  );

  const allFields = { ...selectedFields, ...defaultContentFields };

  return Object.keys(allFields).reduce(
    (acc, fieldKey) =>
      !forbiddenContentFields.includes(fieldKey)
        ? { ...acc, [fieldKey]: allFields[fieldKey] }
        : acc,
    {}
  );
};

export const useInitData = () => {
  const [, setMassActionState] = useMassActionState();
  const callApi = useApiCaller();

  return async () => {
    const massActionRM = await callApi(massActionApi.getMassActionRequestModel);
    const criterias = await callApi(apiClient.loadRecipeParameters, [massActionRM.id]);
    const defaultContentFields = await callApi(massActionApi.getContentDefaultFields);
    const forbiddenContentFields = await callApi(massActionApi.getContentForbiddenFields);

    setMassActionState(state => ({
      ...state,
      requestModel: massActionRM,
      availableCriterias: massActionUtils.mapAvailableCriterias(criterias),
      mandatoryCriterias: massActionUtils.mapMandatoryCriterias(criterias),
      criteriaByKey: massActionUtils.mapCriteriasByKey(criterias),
      defaultContentFields,
      forbiddenContentFields,
    }));
  };
};

export const useEditMassAction = () => {
  const [massActionState, setMassActionState] = useMassActionState();
  const callApi = useApiCaller();
  return async massAction => {
    // load datas in the redux state
    const isWritable = await callApi(apiClient.campaignIsWritable, [massAction.id]);
    const recipe = await callApi(apiClient.getRecipe, [massAction.recipeId]);
    const setup = await callApi(apiClient.getSetup, [recipe.setupId]);
    const { criteriaByKey, availableResultfields } = massActionState;

    setMassActionState(state => ({
      ...state,
      isWritable,
      currentMassActionId: massAction.id,
      currentName: massAction.name ?? '',
      currentMassActionInitialData: massAction,
      currentCreationDate: massAction.creationDate ?? '',
      currentByPass: massAction.bypass ?? false,
      currentReplay: massAction.replay ?? false,
      currentRecipeId: recipe.id,
      currentSetupId: setup.id,
      selectedCriterias: massActionUtils.getSelectedCriteriasFromValue(setup.value, criteriaByKey),
      selectedResultfields: massActionUtils.getSelectedColumnsFromList(
        JSON.parse(setup.columnList),
        availableResultfields
      ),
    }));
  };
};

export function useCampaignEditor(campaignToEditId) {
  const [loaded, setLoaded] = useState(false);
  const [{ massActions }] = useMassActionState();
  const campaignToEdit = massActions.find(massAction => massAction.id === campaignToEditId);
  const editMassAction = useEditMassAction();

  useDidMount(async () => {
    if (!campaignToEdit) {
      setLoaded(true);
      return;
    }
    await editMassAction(campaignToEdit);
    setLoaded(true);
  }, [campaignToEdit, editMassAction, setLoaded]);

  return loaded;
}

export const useEnableCampaign = () => {
  const [massActionState, setMassActionState] = useMassActionState();
  const { showAlert } = useAlert();
  const setLoader = useLoader();
  const dispatch = useDispatch();
  const callApi = useApiCaller();
  return async massAction => {
    const { massActions } = massActionState;
    const isWritable = await callApi(apiClient.campaignIsWritable, [massAction.id]);
    const asyncFn = async () => {
      setLoader(true);
      const activationResponse = await callApi(apiClient.activateCampaign, [
        massAction.id,
        !massAction.enabled,
      ]);

      if (activationResponse.isSuccess) {
        dispatch(
          enqueueSnackbar({
            message: `${massAction.name}: ${
              !massAction.enabled
                ? i18next.t('smartmessaging.notifications.enabledCampaign')
                : i18next.t('smartmessaging.notifications.disabledCampaign')
            }`,

            options: {
              variant: massAction.enabled ? 'warning' : 'success',
            },
          })
        );
        const upToDateList = [...massActions].map(ma => {
          if (ma.id === massAction.id) {
            return { ...massAction, enabled: !massAction.enabled };
          }
          return ma;
        });
        setMassActionState(state => ({ ...state, massActions: upToDateList }));
      } else {
        showAlert({
          type: 'warning',
          title: i18next.t('smartmessaging.errorMessages.massAction.notActivable.title'),
          msg: i18next.t(
            (activationResponse.error &&
              `smartmessaging.errorMessages.massAction.notActivable.${activationResponse.error}`) ||
              'smartmessaging.errorMessages.massAction.notActivable.message'
          ),
        });
      }
    };
    if (isWritable)
      await asyncFn().finally(() => {
        setLoader(false);
      });
    else
      showAlert({
        type: 'warning',
        title: i18next.t('smartmessaging.campaigns.missingRightAlertTitle'),
        msg: i18next.t('smartmessaging.campaigns.changeNotAllowed'),
      });
  };
};

export const useSaveMassAction = () => {
  const callApi = useApiCaller();
  const [massActionState, setMassActionState] = useMassActionState();

  return async () => {
    const { currentMassActionId, currentSetupId, currentRecipeId } = massActionState;
    const newMassActionState = { ...massActionState };

    const setup = massActionUtils.getSetup(massActionState);
    const setupId = await callApi(massActionApi.saveMassActionSetup, [setup]);
    if (!currentSetupId) {
      newMassActionState.currentSetupId = setupId;
    }

    const recipe = massActionUtils.getRecipe(newMassActionState);
    const recipeId = await callApi(massActionApi.saveMassActionRecipe, [recipe]);
    if (!currentRecipeId) {
      newMassActionState.currentRecipeId = recipeId;
    } // updates the currentRecipeId in  massAction state

    let massAction;
    if (currentMassActionId) {
      const existingMassAction = newMassActionState.massActions.filter(
        ma => ma.id === currentMassActionId
      )[0];

      massAction = {
        ...existingMassAction,
        ...massActionUtils.getMassActionCampaign(newMassActionState),
      };
    } else {
      massAction = massActionUtils.getMassActionCampaign(newMassActionState);
    }

    setMassActionState(() => newMassActionState);

    // this only returns { id, recipeId,  name, creationDate, enabled }
    const upToDateMassAction = await callApi(massActionApi.saveMassAction, [
      { ...massAction, eventDriven: false },
    ]);

    if (!currentMassActionId) {
      setMassActionState(state => ({ ...state, currentMassActionId }));
    }

    const matches = newMassActionState.massActions.filter(ma => ma.id === upToDateMassAction.id);

    if (!matches.length) {
      setMassActionState(() => ({
        ...newMassActionState,
        massActions: [...newMassActionState.massActions].concat({
          ...upToDateMassAction,
          duplicates: JSON.parse(upToDateMassAction.duplicates),
        }),
        currentMassActionInitialData: upToDateMassAction,
      }));
    } else {
      setMassActionState(() => ({
        ...newMassActionState,
        massActions: newMassActionState.massActions.map(ma =>
          ma.id === upToDateMassAction.id
            ? {
                ...upToDateMassAction,
                duplicates: JSON.parse(upToDateMassAction.duplicates),
              }
            : ma
        ),
        currentMassActionInitialData: upToDateMassAction,
      }));
    }
  };
};

export const useCheckEmptyFieldSelection = () => {
  const [massActionState, setMassActionState] = useMassActionState();
  const saveMassAction = useSaveMassAction();
  return async (callback, save) => {
    const isEmpty = getUngroupedSelectedColumns(massActionState.selectedResultfields).length === 0;
    if (isEmpty) {
      setMassActionState(state => ({
        ...state,
        selectedResultfields: {
          contact: [getContactIdResultfield(massActionState.availableResultfields)],
        },
      }));

      if (save) await saveMassAction();
    }
    callback();
  };
};

// controle de la présence de champs dans le contenu qui ne figurent pas dans les colonnes sélectionnées
// update la campagne sur confirmation si des champs présents dans le contenu ne sont pas sélectionnés
export const useCheckContentFieldsAndSelectedColumns = () => {
  const [massActionState, setMassActionState] = useMassActionState();
  const params = useSelector(paramSelectors.getCurrentParams);
  const stopSmsText = useSelector(state => state.app.config.stopSmsText || '');
  const { showAlert } = useAlert();

  return async (content, actionTypeId, callback) => {
    const { selectedResultfields: selectedFields, availableResultfields } = massActionState;
    const isArray = Array.isArray(content);
    const freemarkerToHtml = [ActionType.SMS.id, ActionType.PUSH_XPLORACTIVE.id].includes(
      actionTypeId
    )
      ? contentEditorUtils.smsFreemarkerToHtml
      : contentEditorUtils.freemarkerToHtml;
    const fields = contentEditorUtils.processContentFields(
      getContentFields(massActionState),
      actionTypeId,
      {
        headerFileId: params.headerStoredfileId || null,
        footerFileId: params.footerStoredfileId || null,
      }
    );

    const validity = massActionUtils.getContentValidity({
      actionType: actionTypeId,
      object: isArray
        ? freemarkerToHtml(content[0] ?? '', fields.fieldsModels, params) || ''
        : null,
      content: isArray
        ? freemarkerToHtml(content[1], fields.fieldsModels, params) || ''
        : freemarkerToHtml(content, fields.fieldsModels, params),

      min:
        actionTypeId === ActionType.SMS.id &&
        massActionState.selectedDiffusionType === 'advertising'
          ? stopSmsText.length
          : 0,
    });

    setMassActionState(state => ({ ...state, contentValidity: validity }));

    const fieldsInContent = []
      .concat(content) // --> so content can be an array or a single string in arguments
      .reduce(
        (fieldList, ctn) => fieldList.concat(contentEditorUtils.getContentUsedField(ctn ?? '')),
        []
      );
    if (fieldsInContent.length) {
      const missingFields = utils.arrayDiff(
        fieldsInContent,
        getUngroupedSelectedColumns(massActionState.selectedResultfields)
      );

      if (missingFields.length) {
        // the reducefn that will be used to merge new fields to add and already selectedFields
        const fusionReducer = (fullSelection, [groupName, groupFields]) => {
          if (fullSelection[groupName]) {
            return {
              ...fullSelection,
              [groupName]: [...fullSelection[groupName]].concat(groupFields),
            };
          }
          return {
            ...fullSelection,
            [groupName]: groupFields,
          };
        };
        // the alert confirm callback
        const doConfirm = async () => {
          // prepare fields to be added : add to selection from field string list, like if it where from the setup
          const fieldsToAdd = {
            ...massActionUtils.getSelectedColumnsFromList(missingFields, availableResultfields),
          };

          setMassActionState(state => ({
            ...state,
            selectedResultfields: Object.entries(fieldsToAdd).reduce(fusionReducer, selectedFields),
          }));

          showAlert(null);

          // await dispatch(saveMassAction(showAlert)); // all new data is set into state so we trigger this action to update remote campaign data
          callback(
            contentEditorUtils.processContentFields(
              getContentFields(massActionState),
              actionTypeId,
              {
                headerFileId: params.headerStoredfileId || null,
                footerFileId: params.footerStoredfileId || null,
              }
            )
          );
        };

        showAlert({
          type: 'warning',
          title: i18next.t('smartmessaging.errorMessages.warning.title'),
          msg: `${i18next.t(
            'smartmessaging.campaignEditor.confirmAutoAddColumns'
          )} : ${missingFields
            .map(mf => i18next.t(`smartmessaging.resultfield.${mf}`))
            .join(', ')}`,
          onConfirm: doConfirm,
          onDismiss() {
            showAlert(null);
          },
        });
      } else {
        callback(
          contentEditorUtils.processContentFields(getContentFields(massActionState), actionTypeId, {
            headerFileId: params.headerStoredfileId || null,
            footerFileId: params.footerStoredfileId || null,
          })
        );
      }
    } else {
      callback(
        contentEditorUtils.processContentFields(getContentFields(massActionState), actionTypeId, {
          headerFileId: params.headerStoredfileId || null,
          footerFileId: params.footerStoredfileId || null,
        })
      );
    }
  };
};

export const useLoadAvailableResultfields = () => {
  const [, setMassActionState] = useMassActionState();
  const callApi = useApiCaller();

  return async () => {
    const resultFields = await callApi(massActionApi.getAvailableResultfields);
    setMassActionState(state => ({
      ...state,
      availableResultfields: massActionUtils.mapAvailableResultfields(resultFields),
    }));
  };
};

export const useLoadMassActions = () => {
  const [, setMassActionState] = useMassActionState();
  const callApi = useApiCaller();

  return async () => {
    const massActions = await callApi(massActionApi.getMassActions);
    setMassActionState(state => ({
      ...state,
      massActions: massActions.map(c => ({ ...c, duplicates: JSON.parse(c.duplicates) })),
    }));
  };
};

export const useSendContent = () => {
  const [massActionState] = useMassActionState();
  const callApi = useApiCaller();
  const { showAlert } = useAlert();
  const setLoader = useLoader();
  const dispatch = useDispatch();
  const mfaActivated = useSelector(state => state.app.config.mfaActivated ?? true);
  const existingCodeNotification = useSelector(state => state.auth.currentCodeNotification);

  return async (actionTypeId, callback) => {
    const {
      currentMassActionId,
      selectedDiffusionType,
      currentRecipeId,
      contentValidity,
    } = massActionState;

    const doSend = async (targetNumber, codeNotificationId = null, verificationCodeRef = null) => {
      setLoader(true);

      const success = await callApi(
        massActionApi.sendContent,
        [
          currentMassActionId,
          selectedDiffusionType,
          actionTypeId,
          targetNumber,
          codeNotificationId,
          verificationCodeRef?.current?.getCode() ?? null,
        ],
        (error, _, defaultErrorHandler) => {
          setLoader(false);
          // Handle invalid verification code error
          if (
            error?.response?.status === 400 &&
            (error?.response?.data?.message === 'api.error.code-notification.too-much-try' ||
              error?.response?.data?.message === 'smartmessaging.massAction.error.2fa.failed')
          ) {
            showAlert({
              title: i18next.t('smartmessaging.errorMessages.warning.title'),
              msg: i18next.t(error.response.data.message),
            });
            dispatch({ type: 'CLEAR_CODE_NOTIFICATION' });
            return Promise.reject();
          } else if (
            error?.response?.status === 400 &&
            error?.response?.data?.message.includes('api.error.code-notification')
          ) {
            // Update 2FA verification dialog with an error message
            verificationCodeRef.current.setError(i18next.t(error.response.data.message));
            return Promise.reject();
          }
          return defaultErrorHandler();
        }
      );

      setLoader(false);

      // Clear current code notification after use
      dispatch({ type: 'CLEAR_CODE_NOTIFICATION' });

      if (success === true) {
        showAlert({
          title: i18next.t('smartmessaging.diffusionSuccess.title'),
          msg: i18next.t('smartmessaging.diffusionSuccess.message'),
        });
        callback();
      } else {
        showAlert({
          title: i18next.t('smartmessaging.diffusionFailure.title'),
          msg: i18next.t('smartmessaging.diffusionFailure.message'),
        });
      }
    };

    const show2FAVerification = async (codeNotificationId, targetNumber, verificationCodeRef) => {
      showAlert({
        title: i18next.t('smartmessaging.diffusion2FA.title'),
        customContent: <VerificationCodeForm ref={verificationCodeRef} />,
        allowBackdropClose: false,
        onConfirm: async () => {
          const verificationCode = verificationCodeRef.current?.getCode() ?? '';
          if (verificationCode.length !== 6) {
            verificationCodeRef.current?.setError(
              i18next.t('smartmessaging.errorMessages.code6Digits')
            );
            return;
          }

          await doSend(targetNumber, codeNotificationId, verificationCodeRef);
        },
        onDismiss() {
          showAlert(null);
        },
      });
    };

    const doProceed = async () => {
      const verificationCodeRef = React.createRef();
      setLoader(true);
      const { targetNumber, smsTotal } = await callApi(massActionApi.actionCoTargetNumber, [
        currentRecipeId,
        selectedDiffusionType,
        actionTypeId,
      ]).finally(() => setLoader(false));

      let confirmMsg = `${i18next.t('smartmessaging.diffusionConfirm.message')} : ${targetNumber}`;
      if (actionTypeId === ActionType.SMS.id) {
        confirmMsg += `<br/>${i18next.t(
          'smartmessaging.diffusionConfirm.smsNumber'
        )} : ${smsTotal}`;
      }

      showAlert({
        title: i18next.t('smartmessaging.diffusionConfirm.title'),
        msg: confirmMsg,
        onConfirm: async () => {
          showAlert(null);

          if (mfaActivated) {
            // Check if a valid (non-expired) codeNotification already exists
            const FIVE_MINUTES_MS = 5 * 60 * 1000;

            const isCodeNotificationValid =
              !!existingCodeNotification &&
              Date.now() - new Date(existingCodeNotification.createdAt).getTime() < FIVE_MINUTES_MS;

            if (isCodeNotificationValid) {
              // Reuse the existing valid code
              show2FAVerification(existingCodeNotification.id, targetNumber, verificationCodeRef);
            } else {
              // Send a new verification code request
              setLoader(true);
              try {
                const response = await callApi(massActionApi.sendVerificationCode, [
                  currentMassActionId,
                  actionTypeId,
                ]);
                const codeNotificationId = response.id.split('/').pop();

                // Store the complete object with ID and creation timestamp
                dispatch({
                  type: 'SET_CODE_NOTIFICATION',
                  value: {
                    id: codeNotificationId,
                    createdAt: response.createdAt,
                  },
                });

                setLoader(false);
                show2FAVerification(codeNotificationId, targetNumber, verificationCodeRef);
              } catch (error) {
                setLoader(false);
                showAlert({
                  type: 'warning',
                  title: i18next.t('smartmessaging.errorMessages.warning.title'),
                  msg: i18next.t(error.response.data.message),
                });
              }
            }
          } else {
            // 2FA disabled: send directly without verification code
            await doSend(targetNumber);
          }
        },
        onDismiss() {
          showAlert(null);
        },
      });
    };

    const businessContentAlertCfg = { alert: false, messages: [] };
    if (!contentValidity.hasOptoutLink && actionTypeId === ActionType.EMAIL.id) {
      businessContentAlertCfg.alert = true;
      businessContentAlertCfg.messages.push(
        i18next.t('smartmessaging.contentEditor.mail.optoutLinkMissing')
      );
    }
    if (
      ['legal', 'news'].indexOf(selectedDiffusionType) !== -1 &&
      actionTypeId !== ActionType.PROSPECTING.id
    ) {
      businessContentAlertCfg.alert = true;
      businessContentAlertCfg.messages.push(
        i18next.t('smartmessaging.contentEditor.marketingWarningContent', {
          diffusionType: i18next.t(`smartmessaging.diffusionType.${selectedDiffusionType}`),
        })
      );
    }

    if (businessContentAlertCfg.alert) {
      showAlert({
        title: i18next.t('smartmessaging.diffusionConfirm.title'),
        msg: `<ul>${businessContentAlertCfg.messages.map(msg => `<li>${msg}</li>`).join()}</ul>`,
        onConfirm: () => doProceed(),
        onDismiss() {
          showAlert(null);
        },
      });
    } else doProceed();
  };
};

export const useSaveContent = () => {
  const [massActionState, setMassActionState] = useMassActionState();
  const callApi = useApiCaller();
  const { showAlert } = useAlert();
  return async (actionType, callback) => {
    const { currentCampaignAction, editor, objectEditor, commercialRelaunch } = massActionState;
    async function saveEmail(isMemberNotification) {
      const storedFileId = await callApi(apiClient.upload, [
        contentEditorUtils.htmlToFreemarker(null, editor, !isMemberNotification),
        'text/html',
      ]);

      let object = contentEditorUtils.htmlToFreemarker(objectEditor.getData());

      const objectContainsHtml = !isMemberNotification && /<[^>]*>/g.test(object);
      if (objectContainsHtml) {
        object = object.replace(/<[^>]*>/g, '');
      }

      const message = await callApi(apiClient.insertOrUpdateMessage, [
        {
          id: (currentCampaignAction && currentCampaignAction.messageId) || null,
          contentStoredFileId: storedFileId,
          object,
        },
      ]);

      const upToDateCampaignAction = await callApi(apiClient.insertOrUpdateCampaignAction, [
        {
          id: (currentCampaignAction && currentCampaignAction.id) || null,
          actionTypeId: isMemberNotification
            ? ActionType.MEMBER_NOTIFICATION.id
            : ActionType.EMAIL.id,
          campaignId: massActionState.currentMassActionId,
          messageId: message.id,
          language: currentCampaignAction.language,
        },
      ]);
      return { upToDateCampaignAction, warnings: { objectContainsHtml } };
    }

    async function saveSMS() {
      const storedFileId = await callApi(apiClient.upload, [
        contentEditorUtils.htmlToFreemarkerSms(editor.getData()),
        'text/html',
      ]);

      const message = await callApi(apiClient.insertOrUpdateMessage, [
        {
          id: (currentCampaignAction && currentCampaignAction.messageId) || null,
          contentStoredFileId: storedFileId,
        },
      ]);

      const upToDateCampaignAction = await callApi(apiClient.insertOrUpdateCampaignAction, [
        {
          id: (currentCampaignAction && currentCampaignAction.id) || null,
          actionTypeId: ActionType.SMS.id,
          campaignId: massActionState.currentMassActionId,
          messageId: message.id,
          language: currentCampaignAction.language,
        },
      ]);
      return upToDateCampaignAction;
    }

    async function saveXplActivePush() {
      const storedFileId = await callApi(apiClient.upload, [
        contentEditorUtils.htmlToFreemarkerSms(editor.getData()),
        'text/html',
      ]);

      const message = await callApi(apiClient.insertOrUpdateMessage, [
        {
          id: (currentCampaignAction && currentCampaignAction.messageId) || null,
          contentStoredFileId: storedFileId,
          object: contentEditorUtils.htmlToFreemarkerSms(objectEditor.getData()),
        },
      ]);

      const upToDateCampaignAction = await callApi(apiClient.insertOrUpdateCampaignAction, [
        {
          id: (currentCampaignAction && currentCampaignAction.id) || null,
          actionTypeId: ActionType.PUSH_XPLORACTIVE.id,
          campaignId: massActionState.currentMassActionId,
          messageId: message.id,
          language: currentCampaignAction.language,
        },
      ]);
      return upToDateCampaignAction;
    }

    let upToDateCmpAction = null;
    let warnings = {};
    switch (actionType) {
      case ActionType.SMS.id:
        upToDateCmpAction = await saveSMS();
        break;
      case ActionType.EMAIL.id:
      case ActionType.MEMBER_NOTIFICATION.id:
        ({ upToDateCampaignAction: upToDateCmpAction, warnings } = await saveEmail(
          ActionType.MEMBER_NOTIFICATION.id === actionType
        ));
        break;
      case ActionType.PUSH_XPLORACTIVE.id:
        upToDateCmpAction = await saveXplActivePush();
        break;
      case ActionType.PROSPECTING.id:
        upToDateCmpAction = await onSaveCommercialRelaunchAction(
          commercialRelaunch,
          massActionState.currentMassActionId,
          commercialRelaunch.messageId,
          currentCampaignAction.id,
          callApi
        );

        break;
      default:
        break;
    }
    if (callback) callback();
    if (warnings.objectContainsHtml) {
      showAlert({
        type: 'warning',
        title: utils.getLang('smartmessaging.errorMessages.warning.title'),
        msg: utils.getLang('smartmessaging.contentEditor.sms.objectContainsHtmlWarning'),
      });
    }
    setMassActionState(state => ({
      ...state,
      currentCampaignAction: upToDateCmpAction,
      contentHasChanges: false,
      forceIsNewContent: false,
    }));
  };
};

export const useMassActionCsvExport = () => {
  const callApi = useApiCaller();
  const [massActionState] = useMassActionState();
  const { showAlert } = useAlert();
  const setLoader = useLoader();

  return async () => {
    const { currentRecipeId } = massActionState;
    setLoader(true);
    const exportFileId = await callApi(massActionApi.massActionCsvExport, [currentRecipeId]);

    if (exportFileId && exportFileId !== -1) {
      await utils.downloadPrivateStoredFile(exportFileId, callApi);
      setLoader(false);
    } else {
      setLoader(false);
      showAlert({
        msg: i18next.t('smartmessaging.noData'),
      });
    }
  };
};

export const useValidateMassActionName = () => {
  const [massActionState] = useMassActionState();
  const { showAlert } = useAlert();
  const callApi = useApiCaller();
  return async callback => {
    let nameIsValid = !!massActionState.currentName;

    if (nameIsValid) {
      let nameIsDuplicate = false;
      const testDuplicateName =
        !massActionState.currentMassActionInitialData ||
        !(massActionState.currentMassActionInitialData.name === massActionState.currentName);

      if (testDuplicateName)
        nameIsDuplicate = await callApi(apiClient.checkDuplicateName, [
          massActionState.currentName,
        ]);

      if (nameIsDuplicate) {
        nameIsValid = false;
        showAlert({
          type: 'warning',
          title: i18next.t('smartmessaging.errorMessages.warning.title'),
          msg: i18next.t('smartmessaging.errorMessages.campaign.duplicateName.message'),
        });
      }
      if (nameIsValid && callback) callback();
    }
  };
};

export const clearEditedData = state => ({
  ...state,
  selectedCriterias: massActionUtils.addAllMandatories(state),
  selectedResultfields: {},
  currentMassActionInitialData: null,
  currentMassActionId: null,
  currentCreationDate: null,
  currentRecipeId: null,
  currentSetupId: null,
  currentName: '',
  currentCampaignAction: null,
  selectedDiffusionType: null,
  editor: null,
  objectEditor: null,
  contentHasChanges: false,
  dataLoadingChangeEventHandled: 0,
  isWritable: true,
  currentReplay: null,
  currentByPass: null,
});

export const useResetContentEditorFlowData = () => {
  const [, setMassActionState] = useMassActionState();
  return async () => {
    setMassActionState(state => ({
      ...state,
      contentHasChanges: false,
      dataLoadingChangeEventHandled: 0,
    }));
  };
};

export const useDeleteCmpAction = () => {
  const { showAlert } = useAlert();
  const dispatch = useDispatch();
  const callApi = useApiCaller();
  const setLoader = useLoader();

  return async (cmpActionId, callback) => {
    const doDelete = async id => {
      setLoader(true);
      await callApi(apiClient.deleteCampaignAction, [id]).finally(() => {
        setLoader(false);
      });
      if (callback) callback();
      dispatch(
        enqueueSnackbar({
          message: i18next.t('smartmessaging.notifications.deletedContent'),
          options: {
            variant: 'warning',
          },
        })
      );
    };

    showAlert({
      type: 'warning',
      title: i18next.t('smartmessaging.campaignEditor.confirmDeleteCampaignAction.title'),
      msg: i18next.t('smartmessaging.campaignEditor.confirmDeleteCampaignAction'),
      onConfirm() {
        showAlert(null);
        doDelete(cmpActionId).finally(() => setLoader(false));
      },
      onDismiss() {
        showAlert(null);
      },
    });
  };
};
